import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { isNative, startNativeWatch, stopNativeWatch } from "./nativeGeo";

const QKEY = "hr_geo_queue";
const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => localStorage.setItem(QKEY, JSON.stringify(q.slice(-50)));

export const getFix = (timeout = 10000) => new Promise(res => {
  if (!navigator.geolocation) return res({ geo_error: "geolocation unsupported" });
  navigator.geolocation.getCurrentPosition(
    p => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy), speed: p.coords.speed, ts: new Date(p.timestamp).toISOString(), mock: !!(p.coords.__mock || p.mocked) }),
    e => res({ geo_error: e.code === 1 ? "permission denied" : e.code === 2 ? "gps unavailable" : "gps timeout", code: e.code }),
    { enableHighAccuracy: true, timeout, maximumAge: 15000 });
});

/** Heartbeat geofence client: verifies position every `heartbeatSec` while on duty; queues pings offline; reports verdicts. */
export function useGeofence({ enabled, heartbeatSec = 120, onDuty, onAutoOff, onVerdict }) {
  const [verdict, setVerdict] = useState(null);
  const [perm, setPerm] = useState("unknown");
  const timer = useRef(null); const busy = useRef(false);

  const flush = useCallback(async () => {
    let q = readQ();
    while (q.length) { const p = q[0]; try { await api.post("/hr/me/geo/ping", { ...p, offline_queued: true }); q = q.slice(1); writeQ(q); } catch (e) { if (e.response) { q = q.slice(1); writeQ(q); } else break; } }
  }, []);

  const check = useCallback(async () => {
    if (busy.current || !enabled) return null; busy.current = true;
    try {
      const fix = await getFix();
      if (fix.geo_error) setPerm(fix.code === 1 ? "denied" : "unavailable"); else setPerm("granted");
      await flush();
      try {
        const { data } = await api.post("/hr/me/geo/ping", fix);
        const v = { ...data, fix, at: Date.now() }; setVerdict(v); onVerdict?.(v);
        if (data.auto_off) onAutoOff?.(data);
        return v;
      } catch (e) {
        if (!e.response && onDuty) writeQ([...readQ(), fix]);
        const v = { inside: null, reason: e.response ? "server_error" : "offline", fix, at: Date.now() }; setVerdict(v); return v;
      }
    } finally { busy.current = false; }
  }, [enabled, onDuty, flush, onAutoOff, onVerdict]);

  useEffect(() => {
    if (!enabled) { clearInterval(timer.current); return; }
    check();
    const ms = Math.max(30, onDuty ? heartbeatSec : heartbeatSec * 2) * 1000;
    timer.current = setInterval(check, ms);
    const vis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", vis); window.addEventListener("online", flush);
    return () => { clearInterval(timer.current); document.removeEventListener("visibilitychange", vis); window.removeEventListener("online", flush); };
  }, [enabled, onDuty, heartbeatSec]); // eslint-disable-line

  // Native Android shell: background watcher runs only while on duty, stops at Off Duty.
  useEffect(() => {
    if (!isNative()) return;
    if (enabled && onDuty) startNativeWatch((v) => { setVerdict(v); onVerdict?.(v); if (v.auto_off) onAutoOff?.(v); });
    else stopNativeWatch();
    return () => { if (!onDuty) stopNativeWatch(); };
  }, [enabled, onDuty]); // eslint-disable-line

  useEffect(() => { navigator.permissions?.query({ name: "geolocation" }).then(p => { setPerm(p.state); p.onchange = () => setPerm(p.state); }).catch(() => {}); }, []);
  return { verdict, perm, check, native: isNative() };
}

export const PRESENCE = {
  inside: ["INSIDE OFFICE", "bg-emerald-600 text-white"], outside: ["OUTSIDE OFFICE", "bg-rose-600 text-white"], gps_unavailable: ["GPS UNAVAILABLE", "bg-zinc-700 text-white"],
  low_accuracy: ["GPS ACCURACY LOW", "bg-amber-500 text-zinc-900"], offline: ["OFFLINE · QUEUED", "bg-zinc-500 text-white"], server_error: ["VERIFICATION FAILED", "bg-zinc-500 text-white"], no_office: ["NO OFFICE SET", "bg-zinc-400 text-white"], pending: ["VERIFYING…", "bg-zinc-300 text-zinc-800"],
};
